import { scrollToRef } from '@/common/utils/scroll-to-ref';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScrollToVehicleTileConfig {
  scrollTileRef: React.RefObject<HTMLAnchorElement | null>;
  lastViewedVehicleId?: number;
  handleTilesRendered: () => void;
  storeScrollYPositionLastClickedTile: () => void;
}

export const useScrollToVehicleTile = (): ScrollToVehicleTileConfig => {
  const [haveTilesRendered, setHaveTilesRendered] = useState(false);
  const [lastViewedVehicleId, setLastViewedVehicleId] = useState<number | undefined>(
    undefined
  );
  const scrollTileRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    // On mount, extract the last viewed vehicle id from session storage
    const rawLastViewedVehicleId = localStorage.getItem('lastViewedVehicleId');
    const parsedLastViewedVehicleId = rawLastViewedVehicleId
      ? parseInt(rawLastViewedVehicleId)
      : undefined;
    setLastViewedVehicleId(parsedLastViewedVehicleId);
  }, []);

  useEffect(() => {
    // Because the vehicle tiles only render after the deferred inventory call completes, we need to wait until the tiles have
    // rendered before scrolling to the last viewed vehicle tile
    if (!haveTilesRendered) return;

    // Scroll to the last viewed vehicle tile
    if (scrollTileRef.current && lastViewedVehicleId) {
      scrollToRef(scrollTileRef);
      localStorage.removeItem('lastViewedVehicleId');
      localStorage.removeItem('scrollYPositionLastClickedTile');

      // Early return to avoid pulling the scroll y position from local storage into memory
      return;
    }

    // Scroll to the last scroll y position if the vehicle tile from the previous PDP page is not on inventoryz
    const scrollYPositionLastClickedTile = localStorage.getItem(
      'scrollYPositionLastClickedTile'
    );
    if (scrollYPositionLastClickedTile) {
      window.scrollTo({
        top: parseInt(scrollYPositionLastClickedTile),
        behavior: 'smooth',
      });
      localStorage.removeItem('scrollYPositionLastClickedTile');
    }
  }, [lastViewedVehicleId, haveTilesRendered]);

  const handleTilesRendered = useCallback(() => {
    setHaveTilesRendered(true);
  }, []);

  const storeScrollYPositionLastClickedTile = useCallback(() => {
    localStorage.setItem('scrollYPositionLastClickedTile', window.scrollY.toString());
  }, []);

  return {
    scrollTileRef,
    lastViewedVehicleId,
    handleTilesRendered,
    storeScrollYPositionLastClickedTile,
  };
};
